import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { repriceCartItem } from '../lib/couponPricing.js';
import { trackOpenAIAdsCartAdditions } from '../lib/openaiAdsTelemetry.js';

const CartContext = createContext();

function readStoredState(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch (error) {
    console.error(`Failed to load ${key} from localStorage`, error);
    return fallback;
  }
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Hydrate before children mount: their persistence effects must not overwrite
  // saved state before the provider has read it.
  const [cartItems, setCartItems] = useState(() => {
    const stored = readStoredState('cart', []);
    return Array.isArray(stored) ? stored.map((item, index) => repriceCartItem(item, index)) : [];
  });
  const [payload, setPayload] = useState(() => readStoredState('order_payload', {}));
  const measuredCartRef = useRef(cartItems);
  const pendingSendRef = useRef(null);
  const lastPayloadRef = useRef(null);

  useEffect(() => {
    const previous = measuredCartRef.current;
    measuredCartRef.current = cartItems;
    // Observe committed cart changes, not updater invocations (which React can replay).
    trackOpenAIAdsCartAdditions(previous, cartItems);
  }, [cartItems]);

  const clearCompletedOrderState = (sourcePayload = {}) => {
    const next = { ...(sourcePayload || {}) };
    delete next.idempotency_key;
    delete next.checkout_episode_id;
    delete next.airtable_record_id;
    delete next.airtable_order_id;
    delete next.contact;
    delete next.customer;
    delete next.order;
    delete next.orderId;
    delete next.orderNumber;
    delete next.order_number;
    delete next.couponCode;

    if (next.financial && typeof next.financial === 'object') {
      const financial = { ...next.financial };
      delete financial.dropbox_shared_link;
      delete financial.dropbox_worksheet_link;
      delete financial.dropbox_worksheet_links;
      delete financial.paid;
      delete financial.invrec;
      if (Object.keys(financial).length > 0) next.financial = financial;
      else delete next.financial;
    }

    return next;
  };

  useEffect(() => {
    try {
      const cartToSave = cartItems.map(item => {
        const { uploadedDesigns, ...restOfItem } = item;
        const sanitizedDesigns = {};
        if (uploadedDesigns) {
          for (const key in uploadedDesigns) {
            // Preserve the data URL and name so previews persist, but remove File objects
            const { file, ...restOfDesign } = uploadedDesigns[key] || {};
            const preserved = {};
            if (restOfDesign.url) preserved.url = restOfDesign.url;
            if (restOfDesign.originalUrl) preserved.originalUrl = restOfDesign.originalUrl;
            if (restOfDesign.name) preserved.name = restOfDesign.name;
            sanitizedDesigns[key] = preserved;
          }
        }
        return { ...restOfItem, uploadedDesigns: sanitizedDesigns };
      });
      localStorage.setItem('cart', JSON.stringify(cartToSave));
    try {
      // persist payload as well
      localStorage.setItem('order_payload', JSON.stringify(payload || {}));
    } catch (err) {
      // ignore
    }
    } catch (error) {
      console.error("Failed to save cart to localStorage", error);
    }
  }, [cartItems]);

  // Persist payload whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('order_payload', JSON.stringify(payload || {}));
    } catch (err) {
      console.error('Failed to persist payload', err);
    }
  }, [payload]);

  const addToCart = (item) => {
    const newItem = repriceCartItem({
      id: Date.now().toString(),
      ...item,
      timestamp: new Date().toISOString()
    }, cartItems.length);
    // compute next cart synchronously so we can persist a partial immediately
    setCartItems(prev => {
      const next = [...prev, newItem];
      if (import.meta && import.meta.env && import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug('DEV addToCart payload', newItem, { nextCount: next.length });
      }
      // persist immediately (sanitized) to avoid race conditions when navigating
      try {
        const cartToSave = next.map(i => {
          const { uploadedDesigns, ...restOfItem } = i;
          const sanitizedDesigns = {};
          if (uploadedDesigns) {
            for (const key in uploadedDesigns) {
              const { file, ...restOfDesign } = uploadedDesigns[key] || {};
              const preserved = {};
              if (restOfDesign.url) preserved.url = restOfDesign.url;
              if (restOfDesign.originalUrl) preserved.originalUrl = restOfDesign.originalUrl;
              if (restOfDesign.name) preserved.name = restOfDesign.name;
              sanitizedDesigns[key] = preserved;
            }
          }
          return { ...restOfItem, uploadedDesigns: sanitizedDesigns };
        });
        localStorage.setItem('cart', JSON.stringify(cartToSave));
        // also persist a partial payload containing the sanitized cart so support can recover
        try {
          if (typeof mergePayload === 'function') {
            mergePayload({ cart: cartToSave, contact: (payload && payload.contact) || {} });
          }
        } catch (e) {}
      } catch (error) {
        console.error('Failed to persist cart immediately', error);
      }
      return next;
    });
  };

  const removeFromCart = (itemId) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateCartItem = (itemId, updates) => {
    setCartItems(prev => prev.map((item, index) =>
      item.id === itemId ? repriceCartItem({ ...item, ...updates }, index) : item
    ));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  // Payload helpers
  // Configurator persistence effects depend on this callback. Keep its identity
  // stable so a payload update does not trigger those effects again.
  const mergePayload = useCallback((partial) => {
    setPayload(prev => {
      const next = { ...(prev || {}), ...(partial || {}) };
      try {
        localStorage.setItem('order_payload', JSON.stringify(next));
      } catch (err) {}

      // backend removed: no server beacons; localStorage persistence only
      return next;
    });
  }, []);

  // backend removed: no unload beacons

  const clearPayload = () => {
    setPayload({});
    try { localStorage.removeItem('order_payload'); } catch (e) {}
  };

  const resetOrderSession = () => {
    setPayload((prev) => {
      const next = clearCompletedOrderState(prev || {});
      try { localStorage.setItem('order_payload', JSON.stringify(next)); } catch (err) {}
      return next;
    });
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => {
      // Support legacy single-sizeMatrix and new multi-color sizeMatrices
      if (item.sizeMatrices && typeof item.sizeMatrices === 'object') {
        const sum = Object.values(item.sizeMatrices).reduce((s, matrix) => {
          if (!matrix) return s;
          return s + Object.values(matrix).reduce((ss, q) => ss + (q || 0), 0);
        }, 0);
        return total + sum;
      }
      const itemTotal = Object.values(item.sizeMatrix || {}).reduce((sum, qty) => sum + (qty || 0), 0);
      return total + itemTotal;
    }, 0);
  };

  const getTotalPrice = () => {
    if (cartItems.some((item) => item.pricingError)) return null;
    return cartItems.reduce((total, item) => total + (Number(item.totalPrice) || 0), 0);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateCartItem,
      clearCart,
      getTotalItems,
      getTotalPrice
      ,
      payload,
      setPayload,
      mergePayload,
      clearPayload,
      resetOrderSession
    }}>
      {children}
    </CartContext.Provider>
  );
};
